// Silent push re-subscribe.
//
// Run on app open when Notification.permission === "granted".
// If the cached pushSubscription is missing OR was built against a different
// applicationServerKey than the current VAPID_PUBLIC_KEY, unsubscribe and
// re-subscribe with the new key, then upsert the row via pushSubscribe().
// Never prompts the user — no permission request is triggered here.

import { VAPID_PUBLIC_KEY } from "@/lib/notifications-shared";
import { pushSubscribe } from "@/lib/push-public.functions";
import { storeResubscribeToken } from "@/knowmeno/pushTokenStore";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export async function silentResubscribeIfNeeded(
  subscribeFn: (args: { data: { endpoint: string; p256dh: string; auth: string; userAgent?: string } }) => Promise<{ ok?: boolean; id?: string; resubscribeToken?: string } | undefined>,
): Promise<void> {
  if (typeof window === "undefined") return;
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
  if (Notification.permission !== "granted") return;

  const reg = await navigator.serviceWorker.ready;
  const expectedKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

  let sub = await reg.pushManager.getSubscription();
  let needsRefresh = false;
  if (!sub) {
    needsRefresh = true;
  } else {
    const opts = sub.options;
    const existingKey = opts?.applicationServerKey
      ? new Uint8Array(opts.applicationServerKey as ArrayBuffer)
      : null;
    if (!existingKey || !bytesEqual(existingKey, expectedKey)) {
      needsRefresh = true;
    }
  }

  if (!needsRefresh) return;

  try {
    if (sub) {
      try { await sub.unsubscribe(); } catch {}
    }
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: expectedKey as BufferSource,
    });
    const json = sub.toJSON() as any;
    if (!json?.endpoint || !json?.keys?.p256dh || !json?.keys?.auth) {
      throw new Error("Browser returned an incomplete push subscription.");
    }
    const result = await subscribeFn({
      data: {
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        userAgent: navigator.userAgent.slice(0, 256),
      },
    });
    if (result?.resubscribeToken) {
      try { await storeResubscribeToken(json.endpoint, result.resubscribeToken); } catch {}
    }
    console.info("[push] silent resubscribe complete");
  } catch (e) {
    console.warn("[push] silent resubscribe failed", e);
  }
}

// Re-export so callers can wire useServerFn(pushSubscribe) without an extra import line.
export { pushSubscribe };
